/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {FcorgRegionMapModel} from '../models/fcorg-region-map.model';
import {GfsUserDtoModel} from '../models/gfs-user-dto.model';
import {UserPreference} from '../models/user-preference.model';
import {User} from '../models/user.model';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})
export class GfsUserService {

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * gets user by gmid from API url
   * @return Observable<User>
   */
  getUserByGmid(gmid: string): Observable<User> {
    return this.http.get<User>(`${this.configService.baseApiUrl}users/${gmid}`)
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Updates the user's last login time to the current time
   * @param gmid the user's GMID
   */
  updateLastLogin(gmid: string): Observable<any> {
    return this.http.patch<any>(`${this.configService.baseApiUrl}users/last-login/${gmid}`, {})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * gets all users from API url
   * @return Observable<GfsUserDtoModel[]>
   */
  getAllUsers(): Observable<GfsUserDtoModel[]> {
    return this.http.get<any>(`${this.configService.baseApiUrl}users`)
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * gets a user map from API url
   * @param gmid - gmid string to send to backend
   * @return Observable<FcorgRegionMapModel[]>
   */
  getUserMap(gmid: string): Observable<FcorgRegionMapModel[]> {
    return this.http.get<any>(`${this.configService.baseApiUrl}users/${gmid}/user-map`)
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * sends a delete request to delete a user from database through the API url
   * @param gfsUser - the user to send to the backend
   * @return Observable<GfsUserDtoModel>
   */
  deleteUser(gfsUser: GfsUserDtoModel): Observable<GfsUserDtoModel> {
    return this.http.request<any>('DELETE', `${this.configService.baseApiUrl}users`, {body: gfsUser})
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * sends a post request to add a user through the API url
   * @param gfsUser - the user to send to the backend
   * @return Observable<GfsUserDtoModel>
   */
  addUser(gfsUser: GfsUserDtoModel): Observable<GfsUserDtoModel> {
    return this.http.post<any>(`${this.configService.baseApiUrl}users`, gfsUser)
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * sends a put request to edit a user through the API url
   * @param gfsUser - user to send to the backend
   * @return Observable<any>
   */
  editUser(gfsUser: GfsUserDtoModel): Observable<any> {
    return this.http.put<any>(`${this.configService.baseApiUrl}users`, gfsUser)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to get the user report in an Excel file
   * @return Observable<any>
   */
  getUserReport(): Observable<any> {
    const headers = new HttpHeaders();
    headers['Content-Disposition'] = 'attachment; filename=userReport.xlsx';
    return this.http.get(`${this.configService.baseApiUrl}users/report`, {headers, responseType: 'blob'})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets the user preferences for a given GMID
   * @param gmid - The GMID of the user whose preferences are to be retrieved
   */
  getUserPreferences(gmid: string): Observable<any> {
    const params = new HttpParams().set('gmid', gmid);
    return this.http.get<any>(`${this.configService.baseApiUrl}users/preferences`, {params})
      .pipe(catchError(myError => this.handleError(myError)));
  }

  saveUserPreferences(userPreferences: UserPreference[]): Observable<any> {
    const headers = new HttpHeaders({'Content-Type': 'application/json'});
    return this.http.post<any>(`${this.configService.baseApiUrl}users/preferences`, userPreferences, {headers})
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   */
  private handleError(errorResponse: HttpErrorResponse): Observable<any> {
    let errorMessage = 'An unexpected error occurred';
    if (errorResponse.error && errorResponse.error.message) {
      errorMessage = errorResponse.error.message;
    }
    return throwError(errorMessage);
  }
}
